import React from 'react';
import styled from '@emotion/styled';
import scrollTo from 'gatsby-plugin-smoothscroll';
import { Layout } from 'layouts';
import {
  PageHeader,
  TechnologyPartners,
  BrandsWorkedWithUs,
} from '@components';
import {
  StrategyIcon,
  DesignIcon,
  DevelopIcon,
  ScaleIcon,
  SupportIcon,
  GrowIcon,
  MoreIcon,
} from '@components/Svg';
import Heading from '@components/pageHeading';
import PageNavigation from '@components/pageNavigation';
import pageContent from '@constants/services';
import headingIcon from '../../static/icons/Services_heading_Icon.svg';
import headerImage from '../../static/header/andres-urena-qSw5XKtUyus-unsplash.jpg';
import strategyImage from '../../static/services/strategy.jpg';
import designImage from '../../static/services/design.jpg';
import developImage from '../../static/services/develop.jpg';
import scaleImage from '../../static/services/scale.jpg';
import supportImage from '../../static/services/support.jpg';
import growImage from '../../static/services/grow.jpg';
import moreImage from '../../static/services/more.jpg';
import { applicationURLs } from '../routes';

/* ~~~~~~~~~~~~~~~~~ Services Links ~~~~~~~~~~~~~~~~~~~~~~~ */
const ServicesSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 30px 0;
  margin-top: 30px;
  width: 100%;
  background: ${(props) => props.theme.colors.white.lightest};
`;
const ServicesContentWrapper = styled.div`
  max-width: 1200px;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const ServicesLinksWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  max-width: 800px;
  flex-wrap: wrap;
  margin: 0 auto;
`;

const ServiceLink = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 25px 50px;
  cursor: pointer;

  svg {
    width: 72px;
    height: 72px;
  }

  &:hover {
    color: ${(props) => props.theme.colors.primary.base};
    svg {
      fill: ${(props) => props.theme.colors.primary.base};
    }
  }
`;
const ServiceLinkTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  margin: 15px 0;

  &:hover {
    color: ${(props) => props.theme.colors.primary.base};
  }
`;

/* ~~~~~~~~~~~~~~~~~ Services ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */

const ServiceSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
`;
const ServiceSummaryRow = styled.div`
  position: relative;
  max-width: 1000px;
  width: 100%;
`;
const ServiceSummaryWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: self-start;
  width: 100%;
  margin: 50px 0;
  padding-left: ${(props) => (props.orientation == 'left' ? '10%' : '50%')};
  @media (max-width: ${(props) => props.theme.breakpoints.m}) {
    padding-left: 10%;
  }
`;
const ServiceTitle = styled.h2`
  font-size: 36px;
  position: relative;
  display: flex;
  align-items: center;
  padding-right: 70px;
  margin-bottom: 15px;

  &:after {
    content: '';
    position: absolute;
    width: 55px;
    right: 0;
    height: 2px;
    margin-top: 2px;
    background: ${(props) => props.theme.colors.primary.base};
  }
`;
const ServiceSummary = styled.p`
  font-size: 16px;
  line-height: 22px;
  width: 70%;
  max-width: 350px;
  margin-bottom: 0;
  color: ${(props) => props.theme.colors.black.base};
`;
const ServiceContentRow = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 100%;
`;
const ServiceRowHighlighted = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  left: ${(props) => (props.orientation == 'left' ? 0 : null)};
  right: ${(props) => (props.orientation == 'left' ? null : 0)};
  background: ${(props) => props.theme.colors.white.lightest};
  z-index: -1;
`;
const ServiceContentContainer = styled.div`
  position: relative;
  max-width: 1000px;
  width: 100%;
`;
const HighlightedContent = styled.div`
  position: absolute;
  top: 0;
  left: ${(props) => (props.orientation == 'left' ? 0 : null)};
  right: ${(props) => (props.orientation == 'left' ? null : 0)};
  bottom: 0;
  width: 73%;
  background: ${(props) => props.theme.colors.white.lightest};
  z-index: -1;
  @media (max-width: ${(props) => props.theme.breakpoints.m}) {
    width: 100%;
  }
`;
const ServiceContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding-left: ${(props) => (props.orientation == 'left' ? '10%' : '50%')};
  @media (max-width: ${(props) => props.theme.breakpoints.m}) {
    flex-direction: column-reverse;
    padding-left: 0px;
  }
`;
const ServiceContent = styled.div`
  display: flex;
  width: ${(props) => (props.orientation == 'left' ? '61%' : '100%')};
  flex-direction: column;
  padding: 25px 0;
  @media (max-width: ${(props) => props.theme.breakpoints.m}) {
    width: 100%;
    padding-left: 40px;
  }
`;
const ServiceListWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  @media (max-width: ${(props) => props.theme.breakpoints.s}) {
    flex-direction: column;
  }
`;
const ServiceNameList = styled.ul`
  width: 100%;
  padding: 0;
  margin: 0;
  list-style: none;
`;
const ServiceNameListItem = styled.li`
  padding: 0 0 0 20px;
  font-size: 16px;
  color: ${(props) => props.theme.colors.black.base};
  font-weight: ${(props) => (props.isTitle ? 'bold' : null)};
  display: flex;
  position: relative;
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    background-color: ${(props) => props.theme.colors.primary.base};
    border-radius: 50%;
    visibility: ${(props) => (props.isTitle ? 'hidden' : 'visible')};
  }
`;
const GetQuoteButton = styled.a`
  display: inline-block;
  font-size: 16px;
  color: ${(props) => props.theme.colors.white.base};
  background: ${(props) => props.theme.colors.primary.base};
  font-weight: bold;
  height: 40px;
  line-height: 40px;
  border-radius: 20px;
  width: 150px;
  margin: 20px 0 10px 0;
  text-align: center;
  transition: all 0.4s;

  &:hover {
    color: ${(props) => props.theme.colors.white.base};
    background: ${(props) => props.theme.colors.primary.light};
  }
`;
const ServiceImage = styled.div`
  position: absolute;
  width: 35%;
  left: ${(props) => (props.orientation == 'left' ? null : 0)};
  right: ${(props) => (props.orientation == 'left' ? 0 : null)};
  height: 220px;
  background: #ddd;
  background-image: url(${(props) => props.src});
  background-size: cover;
  @media (max-width: ${(props) => props.theme.breakpoints.m}) {
    position: static;
    width: 100%;
    height: 300px;
  }
`;

const Services = () => {
  return (
    <Layout themeType="dark">
      <PageHeader image={headerImage} title={'Services'} icon={headingIcon} />

      <Heading title={pageContent.heading} overview={pageContent.overview} />

      <ServicesSection>
        <ServicesContentWrapper>
          <ServicesLinksWrapper>
            <ServiceLink onClick={() => scrollTo('#Strategy')}>
              <StrategyIcon />
              <ServiceLinkTitle>Strategy</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#Design')}>
              <DesignIcon />
              <ServiceLinkTitle>Design</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#Develop')}>
              <DevelopIcon />
              <ServiceLinkTitle>Develop</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#Scale')}>
              <ScaleIcon />
              <ServiceLinkTitle>Scale</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#Support')}>
              <SupportIcon />
              <ServiceLinkTitle>Support</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#Grow')}>
              <GrowIcon />
              <ServiceLinkTitle>Grow</ServiceLinkTitle>
            </ServiceLink>
            <ServiceLink onClick={() => scrollTo('#More')}>
              <MoreIcon />
              <ServiceLinkTitle>More</ServiceLinkTitle>
            </ServiceLink>
          </ServicesLinksWrapper>
        </ServicesContentWrapper>
      </ServicesSection>

      <ServiceSection id="Strategy">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'left'}>
            <ServiceTitle>Strategy</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'left'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'left'} />
            <ServiceContentWrapper orientation={'left'}>
              <ServiceContent orientation={'left'}>
                <ServiceNameList>
                  <ServiceNameListItem>Business Analysis</ServiceNameListItem>
                  <ServiceNameListItem>Product Spec</ServiceNameListItem>
                  <ServiceNameListItem>
                    Technical Architecture
                  </ServiceNameListItem>
                  <ServiceNameListItem>Product Management</ServiceNameListItem>
                </ServiceNameList>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={strategyImage} orientation={'left'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="Design">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'right'}>
            <ServiceTitle>Design</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'right'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'right'} />
            <ServiceContentWrapper orientation={'right'}>
              <ServiceContent orientation={'right'}>
                <ServiceListWrapper>
                  <ServiceNameList>
                    <ServiceNameListItem>User Flows</ServiceNameListItem>
                    <ServiceNameListItem>Journey Maps</ServiceNameListItem>
                    <ServiceNameListItem>Sitemaps</ServiceNameListItem>
                    <ServiceNameListItem>Wireframes</ServiceNameListItem>
                    <ServiceNameListItem>
                      Clickable Prototypes
                    </ServiceNameListItem>
                    <ServiceNameListItem>Style Guides</ServiceNameListItem>
                  </ServiceNameList>
                  <ServiceNameList>
                    <ServiceNameListItem>Brand Identity</ServiceNameListItem>
                    <ServiceNameListItem>Logo Design</ServiceNameListItem>
                    <ServiceNameListItem>Graphic Design</ServiceNameListItem>
                    <ServiceNameListItem>
                      Responsive Interfaces
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Interaction Design
                    </ServiceNameListItem>
                  </ServiceNameList>
                </ServiceListWrapper>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={designImage} orientation={'right'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="Develop">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'left'}>
            <ServiceTitle>Develop</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'left'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'left'} />
            <ServiceContentWrapper orientation={'left'}>
              <ServiceContent orientation={'left'}>
                <ServiceListWrapper>
                  <ServiceNameList>
                    <ServiceNameListItem>
                      iOS App Development
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Android App Development
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      React and React Native Development
                    </ServiceNameListItem>
                    <ServiceNameListItem>Web Development</ServiceNameListItem>
                    <ServiceNameListItem>Mobile First</ServiceNameListItem>
                    <ServiceNameListItem>
                      Quality Assurance Services
                    </ServiceNameListItem>
                  </ServiceNameList>
                  <ServiceNameList>
                    <ServiceNameListItem>
                      eCommerce App Development
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Tech Stack Migration
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Enterprise Solution
                    </ServiceNameListItem>
                    <ServiceNameListItem>Wearables</ServiceNameListItem>
                    <ServiceNameListItem>BlockChain</ServiceNameListItem>
                  </ServiceNameList>
                </ServiceListWrapper>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={developImage} orientation={'left'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="Scale">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'right'}>
            <ServiceTitle>Scale</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'right'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'right'} />
            <ServiceContentWrapper orientation={'right'}>
              <ServiceContent orientation={'left'}>
                <ServiceNameList>
                  <ServiceNameListItem>Cloud Cost Audit</ServiceNameListItem>
                  <ServiceNameListItem>
                    Cloud Security Audit
                  </ServiceNameListItem>
                  <ServiceNameListItem>OnPremise to Cloud</ServiceNameListItem>
                  <ServiceNameListItem>
                    Inter Cloud Migration
                  </ServiceNameListItem>
                  <ServiceNameListItem>
                    Infrastructure as Code
                  </ServiceNameListItem>
                  <ServiceNameListItem>CI/CD Services</ServiceNameListItem>
                </ServiceNameList>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={scaleImage} orientation={'right'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="Support">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'left'}>
            <ServiceTitle>Support</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'left'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'left'} />
            <ServiceContentWrapper orientation={'left'}>
              <ServiceContent orientation={'left'}>
                <ServiceNameList>
                  <ServiceNameListItem>Ongoing Maintenance</ServiceNameListItem>
                  <ServiceNameListItem>Uptime Monitoring</ServiceNameListItem>
                  <ServiceNameListItem>
                    Performance Monitoring
                  </ServiceNameListItem>
                  <ServiceNameListItem>Analytics Reporting</ServiceNameListItem>
                  <ServiceNameListItem>Bug Fixes</ServiceNameListItem>
                  <ServiceNameListItem>
                    Plugin &amp; Security Updates
                  </ServiceNameListItem>
                </ServiceNameList>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={supportImage} orientation={'left'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="Grow">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'right'}>
            <ServiceTitle>Grow</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'right'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'right'} />
            <ServiceContentWrapper orientation={'right'}>
              <ServiceContent orientation={'right'}>
                <ServiceNameList>
                  <ServiceNameListItem>SEO</ServiceNameListItem>
                  <ServiceNameListItem>
                    Social Media Marketing
                  </ServiceNameListItem>
                  <ServiceNameListItem>Paid Ads</ServiceNameListItem>
                  <ServiceNameListItem>App Promotion</ServiceNameListItem>
                </ServiceNameList>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={growImage} orientation={'right'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <ServiceSection id="More">
        <ServiceSummaryRow>
          <ServiceSummaryWrapper orientation={'left'}>
            <ServiceTitle>More</ServiceTitle>
            <ServiceSummary>
              From startegy to development and scale, we design highly engaging
              experiences.
            </ServiceSummary>
          </ServiceSummaryWrapper>
        </ServiceSummaryRow>
        <ServiceContentRow>
          <ServiceRowHighlighted orientation={'left'} />
          <ServiceContentContainer>
            <HighlightedContent orientation={'left'} />
            <ServiceContentWrapper orientation={'left'}>
              <ServiceContent orientation={'left'}>
                <ServiceListWrapper>
                  <ServiceNameList>
                    <ServiceNameListItem isTitle={true}>
                      Integrations
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Payment Integration Services
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Analytics Integration Services
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Auth &amp; SSO Services
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      APM Integration Services
                    </ServiceNameListItem>
                  </ServiceNameList>
                  <ServiceNameList>
                    <ServiceNameListItem isTitle={true}>
                      OutSourced Services
                    </ServiceNameListItem>
                    <ServiceNameListItem>
                      Tech Team Outsourcing
                    </ServiceNameListItem>
                    <ServiceNameListItem>CTO as a Service</ServiceNameListItem>
                  </ServiceNameList>
                </ServiceListWrapper>
                <GetQuoteButton href={applicationURLs.CONTACT_US}>Get a quote</GetQuoteButton>
              </ServiceContent>
              <ServiceImage src={moreImage} orientation={'left'} />
            </ServiceContentWrapper>
          </ServiceContentContainer>
        </ServiceContentRow>
      </ServiceSection>

      <PageNavigation
        prev={pageContent.navigation.prev}
        prevLink={applicationURLs.HOME}
        next={pageContent.navigation.next}
        nextLink={applicationURLs.INDUSTRIES}
      />
      <TechnologyPartners />
      <BrandsWorkedWithUs noBackground />
    </Layout>
  );
};

export default Services;
